import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import * as api from '../../api';
import { Btn, Input, Modal, RNLLoader, SearchableSelect } from '../UI';
import { CommentModal, Pill } from '../materialTakeoff/MTOShared';
import { UNMAPPED_STATE, exportXlsx, fmtQty } from './mmUtils';

const th = { padding: '10px 12px', fontSize: 10, fontWeight: 800, color: '#fff', background: '#0b318bff', border: '1px solid black',
             textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap', textAlign: 'left', position: 'sticky', top: 0, zIndex: 1 };
const td = { padding: '9px 12px', fontSize: 12.5, borderBottom: '1px solid var(--border-light)', verticalAlign: 'top' };
const small = { padding: '4px 10px', fontSize: 11.5 };

const DECISIONS = {
  non_boq: { title: 'Mark as Non-BOQ', message: 'The material is used on this project but belongs to no BOQ line (site overheads, temporary works…).', required: true },
  variation: { title: 'Mark as Variation', message: 'The material is for work outside the contract BOQ — it should be valued as a variation.', required: true },
  deferred: { title: 'Defer mapping', message: 'Keep it on the list, flagged as deferred — e.g. waiting for a revised BOQ or drawings.', required: false },
};

// Choose the BOQ line a material belongs to (the keyword suggestion is pre-selected), then open the mapper.
function PickBoqLine({ projectId, row, onPick, onClose }) {
  const [options, setOptions] = useState(null);
  const [value, setValue] = useState(row.suggested_boq_item_id || '');
  useEffect(() => {
    api.listMappingRegister({ project_id: projectId, limit: 5000 })
      .then(r => setOptions((r || []).map(o => ({
        value: o.boq_item_id,
        label: `${o.building ? `${o.building} · ` : ''}${o.boq_code} — ${o.description.slice(0, 80)}${o.status === 'approved' ? ' (approved — reopen to add)' : ''}`,
      }))))
      .catch(() => setOptions([]));
  }, [projectId]);
  return (
    <Modal title={`Map ${row.material_code}`} onClose={onClose} width={680} blur={2}>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-secondary)' }}>
        Which BOQ line does <b>{row.material_name}</b> help execute? The mapper opens with the material already added.
      </p>
      {options === null ? <RNLLoader title="Loading BOQ lines..." /> : (
        <SearchableSelect value={value} onChange={setValue} options={options} dropdownWidth={620} placeholder="— Select the BOQ line —" />
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn disabled={!value} onClick={() => onPick(value)}>Open Mapper</Btn>
      </div>
    </Modal>
  );
}

// Materials moving on the project (requested, ordered or used on site) that no approved mapping covers.
// Mapping is required for traceability, but it never blocks the store or procurement.
export default function MMUnmapped({ projectId, openMapper, canEdit, toast }) {
  const [rows, setRows] = useState(null);
  const [showClassified, setShowClassified] = useState(false);
  const [search, setSearch] = useState('');
  const [deciding, setDeciding] = useState(null);         // { row, decision }
  const [picking, setPicking] = useState(null);           // the row being mapped

  const load = useCallback(() => {
    setRows(null);
    api.clearApiCache();
    api.listUnmappedMaterials(projectId, showClassified)
      .then(r => setRows(r || []))
      .catch(e => { setRows([]); toast?.show?.(e.message || 'Could not load unmapped materials', 'error'); });
  }, [projectId, showClassified, toast]);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (rows || []).filter(r => !q || `${r.material_code} ${r.material_name} ${r.material_category} ${r.buildings.join(' ')}`.toLowerCase().includes(q));
  }, [rows, search]);

  const undo = async (row) => {
    try {
      await api.clearMaterialMappingException(row.exception_id);
      toast?.show?.('Decision undone — the material is back to unmapped', 'success');
      load();
    } catch (e) {
      toast?.show?.(e.message || 'Could not undo', 'error');
    }
  };

  const exportAll = () => exportXlsx(shown, [
    { key: 'material_code', label: 'Material Code' }, { key: 'material_name', label: 'Description' }, { key: 'unit_of_measure', label: 'UOM' },
    { key: 'buildings', label: 'Buildings', value: r => r.buildings.join(', ') }, { key: 'disciplines', label: 'Disciplines', value: r => r.disciplines.join(', ') },
    { key: 'requested', label: 'Requested', value: r => Number(r.requested) }, { key: 'ordered', label: 'Ordered', value: r => Number(r.ordered) },
    { key: 'received', label: 'Received', value: r => Number(r.received) }, { key: 'used', label: 'Used on Site', value: r => Number(r.used) },
    { key: 'state', label: 'State', value: r => UNMAPPED_STATE[r.state]?.label || r.state },
    { key: 'suggested_boq', label: 'Suggested Mapping' }, { key: 'reason', label: 'Reason / Notes' },
  ], 'Unmapped-Materials', 'Unmapped');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Unmapped Materials</h3>
          <p style={{ margin: '3px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
            Materials with project transactions (requests, purchase orders, DMR use) but no approved mapping. Mapping is required for
            traceability — it does not block the store.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={showClassified} onChange={e => setShowClassified(e.target.checked)} /> Show Non-BOQ &amp; Variation
          </label>
          <Btn variant="secondary" onClick={exportAll} disabled={!shown.length}><Download size={14} /> Export Excel</Btn>
        </div>
      </div>
      <div style={{ marginBottom: 12, maxWidth: 420 }}>
        <Input value={search} onChange={setSearch} placeholder="Filter by material code, description, category or building…" />
      </div>

      {rows === null ? <RNLLoader title="Checking materials..." subtitle="Reading requests, purchase orders and DMR use" /> : (
        <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              {['Material Code', 'Description', 'UOM', 'Building', 'Discipline', 'Requested', 'Ordered', 'Received', 'Used on Site', 'Suggested Mapping', 'State / Reason', 'Actions'].map(h => (
                <th key={h} style={{ ...th, textAlign: ['Requested', 'Ordered', 'Received', 'Used on Site'].includes(h) ? 'right' : 'left' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={12} style={{ ...td, textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>
                  {rows.length ? 'No material matches the filter.' : 'Every material moving on this project is covered by an approved mapping. 🎉'}
                </td></tr>
              )}
              {shown.map(r => {
                return (
                  <tr key={r.material_id}>
                    <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{r.material_code}</td>
                    <td style={{ ...td, minWidth: 200 }}>{r.material_name}<div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.material_category}</div></td>
                    <td style={td}>{r.unit_of_measure || '—'}</td>
                    <td style={td}>{r.buildings.join(', ') || '—'}</td>
                    <td style={td}>{r.disciplines.join(', ') || '—'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtQty(r.requested)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtQty(r.ordered)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtQty(r.received)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: Number(r.used) > 0 ? 800 : 400, color: Number(r.used) > 0 ? '#be123c' : undefined }}>{fmtQty(r.used)}</td>
                    <td style={{ ...td, minWidth: 220, fontSize: 12 }}>
                      {r.pending_mapping_refs.length ? <>In {r.pending_mapping_refs.join(', ')} (not approved yet)</>
                        : r.suggested_boq ? <>{r.suggested_boq}<div style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>keyword match on the BOQ description</div></> : '—'}
                    </td>
                    <td style={{ ...td, minWidth: 150 }}>
                      <Pill meta={UNMAPPED_STATE[r.state]} />
                      {r.reason && <div style={{ fontSize: 11.5, marginTop: 4 }}>{r.reason}</div>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {canEdit && (
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', maxWidth: 230 }}>
                          {r.state !== 'non_boq' && r.state !== 'variation' && (
                            <Btn style={small} onClick={() => setPicking(r)}>Map</Btn>
                          )}
                          {r.exception_id ? (
                            <Btn style={small} variant="secondary" onClick={() => undo(r)}>Undo</Btn>
                          ) : (
                            <>
                              <Btn style={small} variant="secondary" onClick={() => setDeciding({ row: r, decision: 'non_boq' })}>Non-BOQ</Btn>
                              <Btn style={small} variant="secondary" onClick={() => setDeciding({ row: r, decision: 'variation' })}>Variation</Btn>
                              <Btn style={small} variant="ghost" onClick={() => setDeciding({ row: r, decision: 'deferred' })}>Defer</Btn>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {picking && (
        <PickBoqLine projectId={projectId} row={picking} onClose={() => setPicking(null)}
                     onPick={(boqItemId) => openMapper(boqItemId, { material: picking.material_id })} />
      )}

      {deciding && (
        <CommentModal
          title={DECISIONS[deciding.decision].title}
          message={`${deciding.row.material_code} — ${deciding.row.material_name}. ${DECISIONS[deciding.decision].message}`}
          label="Reason / notes" required={DECISIONS[deciding.decision].required} confirmLabel="Save"
          onClose={() => setDeciding(null)}
          onConfirm={async (reason) => {
            try {
              await api.setMaterialMappingException({ project_id: projectId, material_id: deciding.row.material_id, decision: deciding.decision, reason });
              toast?.show?.('Saved', 'success');
              setDeciding(null);
              load();
            } catch (e) {
              toast?.show?.(e.message || 'Could not save', 'error');
            }
          }}
        />
      )}
    </div>
  );
}
